import {
  LeadTimeVerdict,
  RequirementStep,
  StepStatus
} from '../types/RequirementSteps.types';
import { parseDateValue } from './Date.util';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const EXPIRING_SOON_DAYS = 30;

export type BiasAuditExpiryKind = 'expiring-soon' | 'expired' | 'none';

export interface BiasAuditExpiryState {
  kind: BiasAuditExpiryKind;
  daysRemaining?: number;
}

// Whole days from `now` until `dateString`, rounded up (a target 29.2 days out
// reads as 30). Returns NaN for an unparseable date.
export const daysUntil = (
  dateString: string,
  now: Date = new Date()
): number => {
  const target = parseDateValue(dateString);
  if (!target) return NaN;
  return Math.ceil((target.getTime() - now.getTime()) / MS_PER_DAY);
};

// Presentation state for a step that carries an expiry (e.g. the annual LL144
// bias-audit report). Only COMPLETED steps can be "expiring soon"; an
// unfinished step stays neutral until the backend marks it EXPIRED. Comparisons
// use absolute time (getTime) so they are timezone/DST safe.
export const getBiasAuditExpiryState = (
  expiresAt: string | undefined,
  status: StepStatus,
  now: Date = new Date()
): BiasAuditExpiryState => {
  const expiry = expiresAt ? parseDateValue(expiresAt) : null;
  const expiryTime = expiry ? expiry.getTime() : NaN;
  const nowTime = now.getTime();
  const hasValidExpiry = !Number.isNaN(expiryTime);

  // Expired: backend-derived EXPIRED status, or a concrete at/past-due date.
  if (status === 'EXPIRED' || (hasValidExpiry && expiryTime <= nowTime)) {
    return { kind: 'expired' };
  }

  // Expiring soon: a COMPLETED step with a future date under 30 days out.
  if (
    status === 'COMPLETED' &&
    hasValidExpiry &&
    expiryTime > nowTime &&
    expiryTime - nowTime < EXPIRING_SOON_DAYS * MS_PER_DAY
  ) {
    return {
      kind: 'expiring-soon',
      daysRemaining: Math.ceil((expiryTime - nowTime) / MS_PER_DAY)
    };
  }

  return { kind: 'none' };
};

// Presentation state for a step carrying a notice lead time (LL144's candidate notice).
//
// Deliberately a thin read of the server's verdict rather than a recomputation: "10 business days"
// needs a holiday calendar, and a second copy of that rule here would drift from the one the API
// enforces. The client's job is to decide how to SAY it, not whether it holds.
//
// - `breached`  the window was missed and nobody has accepted it. Holds the step out of COMPLETED
//               and flags the system. Permanent: a late notice cannot be un-sent, so this does not
//               clear by re-uploading, and the copy should not imply otherwise.
// - `acknowledged` the same breach, explicitly accepted. The step completes and the system is no
//               longer flagged, but the breach is still on record and must still be shown — an
//               accepted breach is not a clean step.
// - `no-use-date` a notice exists but the system has no first-use date, so nothing can be checked
//               yet. Holds the step open too, but it is unfinished rather than breached.
// - `none`      nothing to say, including every step that carries no lead time at all.
export type NoticeLeadTimeKind =
  | 'breached'
  | 'acknowledged'
  | 'no-use-date'
  | 'none';

export interface NoticeLeadTimeState {
  kind: NoticeLeadTimeKind;
  businessDaysGiven?: number;
  requiredBusinessDays?: number;
  earliestCompliantUseDate?: string;
}

export const getNoticeLeadTimeState = (
  leadTime: LeadTimeVerdict | null | undefined,
  acknowledgedAt?: string | null
): NoticeLeadTimeState => {
  if (!leadTime) return { kind: 'none' };

  if (leadTime.status === 'UNKNOWN') {
    return {
      kind: 'no-use-date',
      requiredBusinessDays: leadTime.requiredBusinessDays
    };
  }

  if (leadTime.status === 'NOT_MET') {
    return {
      // An acknowledged breach is still a breach; only the urgency changes.
      kind: acknowledgedAt ? 'acknowledged' : 'breached',
      // `?? undefined` rather than `?? 0`: NOT_MET always carries a number, and coercing a missing
      // one to zero would silently render "0 of 10" for what is actually a malformed payload.
      businessDaysGiven: leadTime.businessDaysGiven ?? undefined,
      requiredBusinessDays: leadTime.requiredBusinessDays,
      earliestCompliantUseDate: leadTime.earliestCompliantUseDate
    };
  }

  return { kind: 'none' };
};

// Whether the pack's obligations form a sequence at all.
//
// `unknown` is a real answer, not a fallback: before the steps arrive - and from an API predating
// the pack's unlock fields - we have not been told, and the list must not assert an order it cannot
// see. Collapsing that into "sequential" is what made LL144's four orderless obligations read as a
// numbered EU workflow.
export type StepOrdering = 'parallel' | 'sequential' | 'unknown';

type OrderingStep = Pick<
  RequirementStep,
  'locked' | 'initiallyUnlocked' | 'requires'
>;

export const getStepOrdering = (steps: OrderingStep[]): StepOrdering => {
  if (steps.length === 0) return 'unknown';

  // The steps endpoint sends a boolean for EVERY step (`packStep?.unlock.initiallyUnlocked ??
  // false`), so `undefined` means the field is absent from the payload, not that the step is gated.
  const declaresUnlock = steps.every(
    (step) => typeof step.initiallyUnlocked === 'boolean'
  );
  if (!declaresUnlock) {
    // A padlock on screen is itself proof of an order; with none, nothing justifies either claim.
    return steps.some((step) => step.locked) ? 'sequential' : 'unknown';
  }

  // LOAD-BEARING, and not obvious: importer/distributor steps are legacy TS constants server-side
  // (`constants/requirements/importer.steps.ts`), NOT pack steps, so the pack lookup misses for them
  // and the endpoint's `?? false` / `?? []` defaults are the only reason those flows read as
  // sequential here. If the backend ever materializes them from a pack, re-check this predicate.
  //
  // Pack validation guarantees `initiallyUnlocked` and a non-empty `requires` are mutually exclusive
  // ("the two state opposite things"), so the `requires` and `locked` clauses are guards against a
  // payload that is not pack-backed rather than independent rules.
  const orderless = steps.every(
    (step) =>
      step.initiallyUnlocked === true && !step.requires?.length && !step.locked
  );

  return orderless ? 'parallel' : 'sequential';
};

// The one sentence the requirement list is allowed to say about ordering.
export const STEPS_DESCRIPTION: Record<StepOrdering, string> = {
  parallel: 'Complete these obligations in any order.',
  sequential: 'Follow these compliance steps in sequential order.',
  unknown: ''
};
